import { type FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { baseApi } from "~/app/store/baseApi";
import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import type { WishlistListResponseDTO } from "~/features/wishlist/types/dto";
import type { WishlistsOnlyResponse } from "~/features/wishlist/api/fetchWishlists";
import type { WishlistEntryModel, WishlistModel } from "~/features/wishlist/types/model";
import { addBaseProductDataAction } from "~/domains/product/redux/basicProductActions";
import { resolveAddCartToWishlistApiUrl } from "./addCartToWishlistUrl";

const occBaseSiteUrl = (path: string) => occUrl(path);

interface WishlistEntryLookupParams {
  productCode: string;
  wishlistEntryNumber: number;
}

interface UpdateWishlistEntryParams extends WishlistEntryLookupParams {
  id: string;
  quantity?: number;
  comment?: string;
  priceReference?: string;
  optionalParts?: { productCode: string; quantity: number }[];
  newProductCode?: string;
}

const normalizeWishlistEntryNumber = (wishlistEntryNumber?: number) => wishlistEntryNumber ?? 0;

const matchesWishlistEntry = (entry: WishlistEntryModel, lookup: WishlistEntryLookupParams): boolean => {
  const lookupWishlistEntryNumber = normalizeWishlistEntryNumber(lookup.wishlistEntryNumber);
  const entryWishlistEntryNumber = normalizeWishlistEntryNumber(entry.wishlistEntryNumber);

  if (lookupWishlistEntryNumber > 0) {
    return entryWishlistEntryNumber === lookupWishlistEntryNumber && entry.productCode === lookup.productCode;
  }

  return entryWishlistEntryNumber <= 0 && entry.productCode === lookup.productCode;
};

export const wishlistApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getWishlists: builder.query<WishlistsOnlyResponse, void>({
      /**
       * Uses queryFn to manually control caching:
       * - Products are dispatched to productsReducer via addBaseProductDataAction
       * - Only wishlists + wishlistEntries are cached by RTK Query
       * This prevents duplicate storage of product data.
       *
       * Fetches via occFetch (Bearer token + account context) against the
       * OCC endpoint /occ/v2/{baseSiteId}/schaeffler/wishlists.
       */
      queryFn: async (_, { dispatch }) => {
        const url = occUrl("/wishlists");

        const response = await occFetch(url);
        if (!response.ok) {
          return {
            error: { status: response.status, data: await response.text().catch(() => "") } as FetchBaseQueryError,
          };
        }

        const responseBody = (await response.json()) as WishlistListResponseDTO;
        const { transformResponse } = await import("~/features/wishlist/api/fetchWishlists");
        const transformed = transformResponse(responseBody.wishlists ?? []);

        // Dispatch products to products reducer (not cached by RTK Query)
        dispatch(addBaseProductDataAction(transformed.products));

        // Return only wishlists + entries for RTK Query cache
        return {
          data: {
            wishlists: transformed.wishlists,
            wishlistEntries: transformed.wishlistEntries,
          },
        };
      },
      providesTags: ["Wishlist"],
    }),

    // ========================================================================
    // OPTIMISTIC MUTATIONS (no refetch - patch cache directly)
    // ========================================================================

    deleteWishlist: builder.mutation<void, string>({
      queryFn: async (wishlistId) => {
        const url = occBaseSiteUrl(`/wishlist/delete?id=${wishlistId}`);
        const response = await occFetch(url, { method: "DELETE" });
        if (!response.ok) {
          return {
            error: { status: response.status, data: await response.text().catch(() => "") } as FetchBaseQueryError,
          };
        }
        return { data: undefined };
      },
      async onQueryStarted(wishlistId, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          wishlistApi.util.updateQueryData("getWishlists", undefined, (draft) => {
            const wishlist = draft.wishlists.find((wl) => wl.id === wishlistId);
            if (wishlist) {
              // Remove all entries belonging to this wishlist
              draft.wishlistEntries = draft.wishlistEntries.filter((entry) => !wishlist.entryIds.includes(entry.id));
              // Remove the wishlist
              draft.wishlists = draft.wishlists.filter((wl) => wl.id !== wishlistId);
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    removeEntry: builder.mutation<void, { id: string } & WishlistEntryLookupParams>({
      queryFn: async (args) => {
        const { id, ...data } = args;
        const url = occBaseSiteUrl("/wishlist/removeEntry");
        const response = await occFetch(url, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...data,
            wishlistId: id,
            wishlistEntryNumber: normalizeWishlistEntryNumber(data.wishlistEntryNumber),
          }),
        });
        if (!response.ok) {
          return {
            error: { status: response.status, data: await response.text().catch(() => "") } as FetchBaseQueryError,
          };
        }
        return { data: undefined };
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          wishlistApi.util.updateQueryData("getWishlists", undefined, (draft) => {
            // Find and remove the entry
            const entryIndex = draft.wishlistEntries.findIndex((entry) => matchesWishlistEntry(entry, data));
            if (entryIndex !== -1) {
              const entryId = draft.wishlistEntries[entryIndex].id;
              draft.wishlistEntries.splice(entryIndex, 1);

              // Remove entry ID from wishlist's entryIds
              const wishlist = draft.wishlists.find((wl) => wl.id === data.id);
              if (wishlist) {
                wishlist.entryIds = wishlist.entryIds.filter((id) => id !== entryId);
              }
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    updateWishlistEntry: builder.mutation<void, UpdateWishlistEntryParams>({
      queryFn: async (data) => {
        const url = occBaseSiteUrl("/wishlist/updateEntry");
        const response = await occFetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            wishlistId: data.id,
            productCode: data.productCode,
            quantity: data.quantity,
            wishlistEntryNumber: normalizeWishlistEntryNumber(data.wishlistEntryNumber),
            comment: data.comment,
            priceReference: data.priceReference,
            optionalParts: data.optionalParts,
            newProductCode: data.newProductCode,
          }),
        });
        if (!response.ok) {
          return {
            error: { status: response.status, data: await response.text().catch(() => "") } as FetchBaseQueryError,
          };
        }
        return { data: undefined };
      },
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          wishlistApi.util.updateQueryData("getWishlists", undefined, (draft) => {
            const entry = draft.wishlistEntries.find((wishlistEntry) => matchesWishlistEntry(wishlistEntry, data));
            if (entry) {
              if (data.quantity !== undefined) {
                entry.quantity = data.quantity;
              }
              if (data.comment !== undefined) {
                entry.comment = data.comment;
              }
              if (data.priceReference !== undefined) {
                entry.priceReference = data.priceReference;
              }
              if (data.optionalParts !== undefined) {
                entry.optionalParts = data.optionalParts;
              }
              if (data.newProductCode !== undefined) {
                entry.productCode = data.newProductCode;
              }
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    createOrUpdateWishlist: builder.mutation<void, { id?: string; wishlistName: string; description?: string }>({
      queryFn: async (data) => {
        const url = occBaseSiteUrl("/wishlist/createOrUpdate");
        const response = await occFetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...data, wishlistId: data.id }),
        });
        if (!response.ok) {
          return {
            error: { status: response.status, data: await response.text().catch(() => "") } as FetchBaseQueryError,
          };
        }
        return { data: undefined };
      },
      // Conditionally invalidate or patch based on whether this is create or update
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        if (data.id) {
          // UPDATE mode (rename/update comment) - optimistic patch
          const patchResult = dispatch(
            wishlistApi.util.updateQueryData("getWishlists", undefined, (draft) => {
              const wishlist = draft.wishlists.find((wl) => wl.id === data.id);
              if (wishlist) {
                wishlist.name = data.wishlistName;
                if (data.description !== undefined) {
                  wishlist.comment = data.description;
                }
              }
            }),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        } else {
          // CREATE mode - must refetch to get server-generated ID
          try {
            await queryFulfilled;
            dispatch(wishlistApi.util.invalidateTags(["Wishlist"]));
          } catch {
            // Error handled by RTK Query
          }
        }
      },
    }),

    // ========================================================================
    // MUTATIONS THAT REQUIRE REFETCH (server generates IDs)
    // ========================================================================

    addEntry: builder.mutation<
      void,
      {
        id?: string;
        wishlistName?: string;
        productCode: string;
        quantity: number;
        priceReference?: string;
        optionalParts?: { productCode: string; quantity: number }[];
      }
    >({
      queryFn: async (args) => {
        const { id, ...rest } = args;
        const url = occBaseSiteUrl("/wishlist/addEntry");
        const response = await occFetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...rest, wishlistId: id }),
        });
        if (!response.ok) {
          return {
            error: { status: response.status, data: await response.text().catch(() => "") } as FetchBaseQueryError,
          };
        }
        return { data: undefined };
      },
      invalidatesTags: ["Wishlist"],
    }),

    addCart: builder.mutation<Pick<WishlistModel, "name">, { id: string } | { wishlistName: string }>({
      queryFn: async (params) => {
        try {
          const url = await resolveAddCartToWishlistApiUrl();
          const response = await occFetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify("id" in params ? { wishlistId: params.id } : { wishlistName: params.wishlistName }),
          });
          if (!response.ok) {
            return {
              error: { status: response.status, data: await response.text().catch(() => "") } as FetchBaseQueryError,
            };
          }

          const result = (await response.json()) as Partial<Pick<WishlistModel, "name">>;
          if (!result.name) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: "Cart-to-wishlist response did not contain a wishlist name",
              } as FetchBaseQueryError,
            };
          }

          return { data: { name: result.name } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "Cart-to-wishlist request could not be prepared",
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: ["Wishlist"],
    }),

    bulkAddEntry: builder.mutation<
      void,
      { wishlistId?: string; wishlistName?: string; products: { code: string; quantity: number }[] }
    >({
      queryFn: async (data) => {
        const url = occBaseSiteUrl("/wishlist/addEntry/bulk");
        const response = await occFetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            wishlistId: data.wishlistId,
            wishlistName: !data.wishlistId ? data.wishlistName : undefined,
            products: data.products,
          }),
        });
        if (!response.ok) {
          return {
            error: { status: response.status, data: await response.text().catch(() => "") } as FetchBaseQueryError,
          };
        }
        return { data: undefined };
      },
      invalidatesTags: ["Wishlist"],
    }),
  }),
});

export const {
  useGetWishlistsQuery,
  useDeleteWishlistMutation,
  useRemoveEntryMutation,
  useCreateOrUpdateWishlistMutation,
  useUpdateWishlistEntryMutation,
  useAddEntryMutation,
  useAddCartMutation,
  useBulkAddEntryMutation,
} = wishlistApi;
